import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate } from '../../utils/dateFormat';
import { useVatMode } from '../../hooks/useVatMode';
import { HIRE_STATUS_BADGES } from '../../constants/colors';
import { getOrderNumber } from '../../utils/orderTitle';
import { hireOverdueSignalLabel } from '../../utils/hireOverdueSignal';
import { getHireUrgencyBand } from '../../utils/hireUrgency';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';
import { daysDiff } from '../../utils/ganttDates';

// The amber-triangle path from `HireActionModal.js:167`, recoloured for this
// signal (`text-red-600 dark:text-red-400`) — never re-drawn inline elsewhere.
const OVERDUE_TRIANGLE_PATH =
  'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';

const COLUMNS = [
  { key: 'description', label: 'Item', align: 'left' },
  { key: 'supplier_name', label: 'Supplier', align: 'left' },
  { key: 'hire_status', label: 'Status', align: 'center' },
  { key: 'rate_display', label: 'Rate', align: 'right' },
  { key: 'expected_cost', label: 'Expected', align: 'right' },
  { key: 'current_cost', label: 'Cost to Date', align: 'right' },
  // 🔀 Renders `overdue_extra_cost` (the whole-span delta, `hireOverrunDelta`
  // in `server/helpers/hireCost.js`), NOT `overrun_cost` — see the docblock
  // at the cell below and `HireDashboardTable.overrunColumn.test.js`.
  { key: 'overdue_extra_cost', label: 'Overrun', align: 'right' },
  { key: 'days_remaining', label: 'Days', align: 'right' },
  { key: 'requested_by', label: 'Hired By', align: 'left' },
  // 🔀 key is `order_number` so the header sorts by what it shows, and it
  // sorts NUMERICALLY rather than by a raw string (2026-08-12).
  { key: 'order_number', label: 'Order No.', align: 'left' },
];

function computeDaysRemaining(item) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (item.hire_status === 'off_hired') return null;
  if (!item.hire_to) return null;
  const hireTo = new Date(item.hire_to + 'T00:00:00');
  // 🕰️ ROUNDED via `daysDiff`, never ceil'd: both ends are local midnights,
  // so across a clock change the gap is 23 or 25 hours and ceil adds a day
  // (2026-10-20: "7d left" for a hire due back in 6). See
  // `hireDays.clockChange.test.js`.
  const diff = daysDiff(today, hireTo);
  return diff; // positive = days left, negative = days overdue
}

/**
 * 🔴 THE `total` BRANCH GAINED THE WORD "each" (2026-08-20). ⚠️ THIS IS AN
 * ADDITION, NOT A RELABEL — do not read the commit as reverting a label that
 * existed. This branch returned a BARE figure with no qualifier at all, while
 * its two siblings carried "/day" and "/wk", so the `Rate` column showed an
 * unqualified number immediately to the left of the `Expected` column.
 *
 * Since `hireCostForDays` multiplies all three rate columns by `quantity`
 * (see `hireLineQuantity` in `utils/hireCost.js`), a quantity-3 line put
 * "£1,050" beside an Expected of "£3,150" with nothing on screen explaining the
 * factor of three. `total` is the schema DEFAULT for `hire_rate_type`, so this
 * is the ordinary line. No stored value changes.
 *
 * 🪤 "/day" and "/wk" are per-item too and are deliberately UNTOUCHED: a unit
 * rate reads as a unit rate, whereas an unqualified figure beside a column
 * headed `Expected` invites being read as the line's cost. Making this column
 * per-line throughout is a product decision, and is on the backlog.
 */
function formatRate(item, fmt) {
  if (item.hire_rate_type === 'daily' && item.daily_rate) {
    return `${fmt(item.daily_rate)}/day`;
  }
  if (item.hire_rate_type === 'weekly' && item.weekly_rate) {
    return `${fmt(item.weekly_rate)}/wk`;
  }
  if (item.total_hire_rate) {
    return `${fmt(item.total_hire_rate)} each`;
  }
  return '-';
}

/**
 * The Days column's cell colour — DERIVED from the shared
 * `getHireUrgencyBand` band, never a private threshold. Owner ruling,
 * 2026-08-18: "I think red should mean late or today. amber is close." —
 * see `utils/hireUrgency.js`. Text labels are UNCHANGED by this; only the
 * colour class is band-driven, so a days>5 "d left" row loses its green and
 * reads as plain text, matching the band's `null` (no special urgency).
 */
function daysColumnClass(band) {
  if (band === 'red') return 'text-red-600 dark:text-red-400 font-medium';
  if (band === 'amber') return 'text-amber-600 dark:text-amber-400 font-medium';
  return 'text-gray-800 dark:text-gray-200';
}

function getSortValue(item, key) {
  switch (key) {
    case 'rate_display':
      return item.daily_rate || item.weekly_rate || item.total_hire_rate || 0;
    case 'days_remaining':
      return computeDaysRemaining(item) ?? 9999;
    default:
      return item[key] ?? '';
  }
}

/** The Cost to Date figure + its overdue signal — ONE renderer for the table
 *  cell and the phone card (mobile batch item 22). */
function HireCostToDate({ item, overdueLabel, formatMoney }) {
  return (
    <>
      <span
        className={
          overdueLabel
            ? 'inline-flex items-center gap-1 text-red-600 dark:text-red-400'
            : 'text-gray-800 dark:text-gray-200'
        }
        title={overdueLabel || undefined}
      >
        {overdueLabel && (
          <svg
            className="w-4 h-4 inline text-red-600 dark:text-red-400"
            aria-hidden="true"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={OVERDUE_TRIANGLE_PATH} />
          </svg>
        )}
        {formatMoney(item.current_cost)}
      </span>
      {overdueLabel && <span className="sr-only">{overdueLabel}</span>}
    </>
  );
}

/** The Days column — ONE renderer for the table cell and the phone card. */
function HireDaysCell({ item, days }) {
  return (
    item.hire_status === 'off_hired' ? (
      // 🔀 Owner report, 2026-08-31 (Jake Dimmer): the table
      // never showed the date he picked in the off-hire
      // dialog — it is the DEFAULT desktop layout, so a save
      // looked unchanged apart from the badge. `HireCard.js`
      // already shows it, in the same slot this "Days" column
      // occupies (its Rate+Days row, not Status — Status stays
      // the fixed "Off-Hired" label in both layouts). Matches
      // `HireCard.js`'s `Returned ${formatDate(...)}` exactly,
      // including `formatDate`'s own null/''/invalid → '-'
      // fallback, so `actual_return_date`'s three possible
      // states (unset, empty, stored) never need a bespoke
      // falsy test here.
      <span className="text-gray-600 dark:text-gray-300">
        Returned {formatDate(item.actual_return_date)}
      </span>
    ) : days === null ? (
      <span className="text-gray-400 dark:text-gray-500">-</span>
    ) : (
      <span className={daysColumnClass(getHireUrgencyBand(days, item.hire_status))}>
        {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `${days}d left`}
      </span>
    )
  );
}

/**
 * Sortable hire items table for the Department Dashboard.
 * Default sort: days overdue descending (most overdue first).
 */
function HireDashboardTable({ items }) {
  const navigate = useNavigate();
  const { formatMoney } = useVatMode();
  const isPhone = useIsMobileOrderViewport();
  const [sortField, setSortField] = useState('days_remaining');
  const [sortDir, setSortDir] = useState('asc'); // asc puts most negative (most overdue) first

  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    return [...items].sort((a, b) => {
      let aVal = getSortValue(a, sortField);
      let bVal = getSortValue(b, sortField);
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [items, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'days_remaining' ? 'asc' : 'desc');
    }
  };

  if (!items || items.length === 0) {
    return <div className="text-gray-400 dark:text-gray-500 text-sm text-center py-6">No hire items found</div>;
  }

  if (isPhone) {
    /* 📱 PHONE (< md, mobile batch item 22): a card per hire. The 10-column
       table was 1046px inside a 315px scroller at 375. Each card keeps what
       a phone reader acts on — item, status, cost to date (with its overdue
       signal), days — plus supplier and order number as the second line;
       Rate / Expected / Overrun / Hired By stay on the order a tap opens.
       Same default sort (most overdue first); no header to re-sort by. Pinned
       by `HireDashboardTable.phoneCards.test.js`. `relative` on the list is
       the sr-only containment rule below, for the same reason. */
    return (
      <ul className="relative divide-y divide-gray-100 dark:divide-gray-700" data-testid="hire-dashboard-cards">
        {sortedItems.map((item) => {
          const days = computeDaysRemaining(item);
          const badge = HIRE_STATUS_BADGES[item.hire_status] || HIRE_STATUS_BADGES.pending;
          const overdueLabel = hireOverdueSignalLabel(item, formatMoney);
          return (
            <li key={item.item_id}>
              <button
                type="button"
                onClick={() => navigate(`/department/orders?order=${item.order_id}`)}
                className="relative w-full text-left py-2 px-1 min-h-[44px] text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20"
                title={`View order ${getOrderNumber(item)}`}
                data-testid={`hire-dashboard-card-${item.item_id}`}
              >
                <span className="flex items-start gap-2">
                  <span className="flex-1 min-w-0 break-words font-medium text-gray-800 dark:text-gray-200">{item.description || '-'}</span>
                  <span className={`shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>{badge.label}</span>
                </span>
                <span className="mt-1 flex items-center justify-between gap-2 whitespace-nowrap">
                  <span className="font-medium"><HireCostToDate item={item} overdueLabel={overdueLabel} formatMoney={formatMoney} /></span>
                  <HireDaysCell item={item} days={days} />
                </span>
                <span className="mt-0.5 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="min-w-0 truncate">{item.supplier_name || '-'}</span>
                  <span className="shrink-0 font-mono">{getOrderNumber(item) || '-'}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    /* `relative` IS LOAD-BEARING (2026-09-30, real Android bug). The overdue
       cell's `sr-only` label is `position: absolute`; with no positioned
       ancestor its containing block is the VIEWPORT, so it escaped this
       scroller and sat at the table's far right (x≈655 at 360px). Android
       Chrome grows the layout viewport to fit it, and the `fixed bottom-0`
       nav then sits below the screen until you scroll. `relative` keeps it
       inside the scroll box. Pinned by `HireDashboardTable.srOnlyContained.test.js`. */
    <div className="relative overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            {COLUMNS.map(col => (
              <th
                key={col.key}
                className={`py-2 px-2 font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200 whitespace-nowrap text-${col.align}`}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
                {sortField === col.key && (
                  <span className="ml-1">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item, i) => {
            const days = computeDaysRemaining(item);
            const badge = HIRE_STATUS_BADGES[item.hire_status] || HIRE_STATUS_BADGES.pending;
            const overdueLabel = hireOverdueSignalLabel(item, formatMoney);
            return (
              <tr
                key={item.item_id}
                className={`border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}
                onClick={() => navigate(`/department/orders?order=${item.order_id}`)}
                title={`View order ${getOrderNumber(item)}`}
              >
                <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-200 max-w-[180px] truncate" title={item.description}>
                  {item.description || '-'}
                </td>
                <td className="py-2 px-2 text-gray-600 dark:text-gray-400 max-w-[120px] truncate" title={item.supplier_name}>
                  {item.supplier_name || '-'}
                </td>
                <td className="py-2 px-2 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                    {badge.label}
                  </span>
                </td>
                <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {formatRate(item, formatMoney)}
                </td>
                <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {formatMoney(item.expected_cost)}
                </td>
                <td className="py-2 px-2 text-right font-medium whitespace-nowrap">
                  <HireCostToDate item={item} overdueLabel={overdueLabel} formatMoney={formatMoney} />
                </td>
                {/*
                  🔀 Owner-approved scope addition, 2026-09-01: reads
                  `overdue_extra_cost` (the whole-span delta), NOT
                  `overrun_cost` (isolated-gap pricing). `overrun_cost` bills
                  a weekly line's late days ON THEIR OWN, so 2 days late
                  still inside the already-paid week reads +£700 (a whole
                  extra week) where the true exposure is £0. `Number.isFinite`
                  gates the branch — `overdue_extra_cost` is unknowable
                  (`null`) when either span cannot be resolved, and must
                  never render as a crash or a false "-" masquerading as £0;
                  `> 0` never fires on `null`, `undefined` or NaN, so the
                  dash is also the safe fallback for "cannot say".
                */}
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  {Number.isFinite(item.overdue_extra_cost) && item.overdue_extra_cost > 0 ? (
                    <span className="text-red-600 dark:text-red-400 font-medium">+{formatMoney(item.overdue_extra_cost)}</span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  <HireDaysCell item={item} days={days} />
                </td>
                <td className="py-2 px-2 text-gray-600 dark:text-gray-400 max-w-[100px] truncate" title={item.requested_by}>
                  {item.requested_by || '-'}
                </td>
                <td className="py-2 px-2 text-gray-500 dark:text-gray-500 font-mono text-xs">
                  {getOrderNumber(item) || '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default HireDashboardTable;
